import React from 'react';
import { Box, Checkbox, Stack, TextField, Typography } from '@mui/material';
import SaveRounded from '@mui/icons-material/SaveRounded';
import RestartAltRounded from '@mui/icons-material/RestartAltRounded';
import {
  EmptyState, FilterSelect, NumberField, Panel, SettingRow, SettingSwitch, ToolbarButton,
} from '../../../ui-kit';
import { ConfirmDialog } from '../../../components/dialog';
import { useCan } from '../../../ui/PermissionsProvider';
import {
  fetchMcConfiguration, resetMcDemo, saveMcSettings, type McConfiguration, type McSettings, type McTeamMember,
} from '../../../services/missedCallService';
import { useLive } from '../rocco/live';

const MEANS = {
  auto: 'On: the WhatsApp message goes to the client as soon as the call is missed.',
  approve: 'Off: each first message waits under Missed calls until someone approves it.',
};

/**
 * How the Missed Call Follow-up agent is set up, exactly as it runs: what the
 * caller hears, what the first WhatsApp message says, which phone rings, and
 * who takes callbacks in which languages. An admin can change any of it.
 */
export default function ConfigurationPage() {
  const { data, error, loading, reload } = useLive(fetchMcConfiguration, 60_000);
  if (error && !data) return <EmptyState message={error} />;

  return (
    <Stack spacing={2}>
      <SettingsPanel data={data} loading={loading} onSaved={reload} />
      <TeamPanel data={data} loading={loading} onSaved={reload} />

      <Panel title="How a call moves" subtitle="From the phone ringing to the callback on an agent's calendar"
        loading={loading} loadingHeight={300}>
        <Stack>
          {(data?.rules ?? []).map((rule) => (
            <Box key={rule.when} sx={(t) => ({
              display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(200px, 1fr) minmax(200px, 1fr) 2fr' },
              columnGap: 2, rowGap: 0.25, py: 1.25, borderBottom: `1px solid ${t.tokens.border.subtle}`,
              '&:last-of-type': { borderBottom: 'none' },
            })}>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{rule.when}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{rule.then}</Typography>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rule.detail}</Typography>
            </Box>
          ))}
        </Stack>
      </Panel>

      <Panel title="Guardrails" subtitle="Held on every call and every message" loading={loading} loadingHeight={200}>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {(data?.guardrails ?? []).map((rail) => (
            <Stack key={rail.title} spacing={0.25}>
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary, fontWeight: 600 })}>{rail.title}</Typography>
              <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{rail.detail}</Typography>
            </Stack>
          ))}
        </Box>
      </Panel>

      {/* Last, and only for an admin: the call history the demo opens on. */}
      <DemoData calls={data?.demo_calls ?? 0} onReset={reload} />
    </Stack>
  );
}

/** Rebuild the two weeks of call history the demo opens on. Live calls stay as they are. */
function DemoData({ calls, onReset }: { calls: number; onReset: () => Promise<void> }) {
  const canManage = useCan('manage_workspace');
  const [asking, setAsking] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [done, setDone] = React.useState(false);
  if (!canManage) return null;

  const reset = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await resetMcDemo();
      await onReset();
      setDone(true);
    } catch (err) {
      setDone(false);
      setProblem(err instanceof Error ? err.message : 'Could not reset the demo data');
    } finally {
      setBusy(false);
      setAsking(false);
    }
  };

  return (
    <Panel
      title="Demo data"
      subtitle={`${calls.toLocaleString()} calls over the last two weeks`}
      actions={(
        <ToolbarButton variant="outlined" icon={<RestartAltRounded />} onClick={() => setAsking(true)} loading={busy}>
          Reset demo data
        </ToolbarButton>
      )}
    >
      <Stack spacing={0.75}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          Reset builds the last two weeks of calls again, up to now, so the screens open on a full, current week.
          Real calls are kept as they are, and nothing is sent to anyone or added to AgencyZoom.
        </Typography>
        {problem ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
        ) : done ? (
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.success })}>Done. The history now runs up to today.</Typography>
        ) : null}
      </Stack>
      <ConfirmDialog
        open={asking}
        onClose={() => setAsking(false)}
        title="Reset demo data?"
        description={'The last two weeks of demo calls are removed and built again, up to now. '
          + 'Real calls, the settings and who takes callbacks are all kept.'}
        confirmLabel="Reset demo data"
        onConfirm={reset}
        busy={busy}
      />
    </Panel>
  );
}

type Draft = Omit<McSettings, 'time_zone'>;

function SettingsPanel({
  data, loading, onSaved,
}: { data: McConfiguration | null; loading: boolean; onSaved: () => Promise<void> }) {
  const canEdit = useCan('manage_workspace');
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (data && !draft) {
      const { time_zone: _zone, ...rest } = data.settings;
      setDraft(rest);
    }
  }, [data, draft]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setSaved(false);
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  };
  const changed = Boolean(data && draft && Object.keys(draft).some(
    (k) => draft[k as keyof Draft] !== data.settings[k as keyof Draft],
  ));
  const phoneOk = !draft?.team_phone || /^\+\d{8,15}$/.test(draft.team_phone);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setProblem(null);
    try {
      await saveMcSettings({ ...draft, team_phone: draft.team_phone || null });
      await onSaved();
      setSaved(true);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save the settings');
    } finally {
      setSaving(false);
    }
  };

  const field = (id: keyof Draft, label: string, description: string, props: { multiline?: boolean; width?: number } = {}) => (
    <SettingRow label={label} htmlFor={`mc-${id}`} description={description}>
      <TextField
        id={`mc-${id}`}
        size="small"
        value={(draft?.[id] as string | null) ?? ''}
        onChange={(e) => set(id, e.target.value as Draft[typeof id])}
        disabled={!canEdit}
        multiline={props.multiline}
        minRows={props.multiline ? 3 : undefined}
        sx={{ width: { xs: '100%', md: props.width ?? 280 } }}
        error={id === 'team_phone' && !phoneOk}
        helperText={id === 'team_phone' && !phoneOk ? 'Write it with the country code, like +14155550123' : undefined}
      />
    </SettingRow>
  );

  return (
    <Panel
      title="Settings"
      subtitle={data
        ? `${data.agency_number ? `Clients call ${data.agency_number}` : 'No number set for clients to call yet'} · WhatsApp messages come from ${data.whatsapp_from}`
        : ' '}
      loading={loading || !draft}
      loadingHeight={360}
      actions={canEdit ? (
        <ToolbarButton variant="brand" icon={<SaveRounded />} onClick={save} loading={saving} disabled={!changed || !phoneOk}>
          Save
        </ToolbarButton>
      ) : undefined}
    >
      {draft ? (
        <Stack spacing={2}>
          {field('agency_name', 'Agency name', 'Said to the caller and written in every WhatsApp message.')}
          {field('assistant_name', 'Name in the messages', 'Who the WhatsApp messages come from, e.g. "Hi, this is Emma".')}
          {field('team_phone', 'Team phone', 'The phone that rings first. Leave empty and every call is treated as missed.')}
          <SettingRow label="How long it rings" description="Seconds before the call counts as missed. Whoever picks up must press 1, so voicemail never counts.">
            <NumberField label="Seconds" value={draft.ring_seconds} min={5} max={60} step={5} suffix="s" size="small"
              disabled={!canEdit} onChange={(v) => set('ring_seconds', v)} />
          </SettingRow>
          {field('call_message', 'What the caller hears', 'Played when nobody takes the call. {agency} becomes the agency name.',
            { multiline: true, width: 420 })}
          {field('first_message', 'First WhatsApp message', 'Sent in English. {assistant} and {agency} are filled in. After this, we reply in the language the client writes in.',
            { multiline: true, width: 420 })}
          <SettingSwitch
            id="mc-send-automatically"
            label="Send the first message automatically"
            description={MEANS[draft.send_mode]}
            checked={draft.send_mode === 'auto'}
            onChange={(on) => set('send_mode', on ? 'auto' : 'approve')}
            disabled={!canEdit}
          />
          {problem ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
          ) : saved ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.success })}>Saved. The next call uses these settings.</Typography>
          ) : !canEdit ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>Only a workspace admin can change these.</Typography>
          ) : null}
        </Stack>
      ) : null}
    </Panel>
  );
}

/**
 * The languages an agent can be given, by their English names: those most
 * spoken by California's insurance clients. The client's language is read
 * from their WhatsApp reply and matched against these.
 */
const LANGUAGES = [
  'English', 'Spanish', 'Punjabi', 'Hindi', 'Urdu', 'Gujarati', 'Tagalog', 'Vietnamese', 'Mandarin',
  'Cantonese', 'Korean', 'Arabic', 'Farsi', 'Armenian', 'Russian', 'Portuguese', 'French',
];

/** Who takes callbacks, read from AgencyZoom, and the languages each speaks. */
function TeamPanel({
  data, loading, onSaved,
}: { data: McConfiguration | null; loading: boolean; onSaved: () => Promise<void> }) {
  const canEdit = useCan('manage_workspace');
  const [rows, setRows] = React.useState<McTeamMember[] | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);

  React.useEffect(() => { if (data) setRows(data.team); }, [data]);

  const change = (id: number, patch: Partial<McTeamMember>) =>
    setRows((r) => (r ? r.map((m) => (m.id === id ? { ...m, ...patch } : m)) : r));
  const changed = Boolean(data && rows && JSON.stringify(rows) !== JSON.stringify(data.team));
  const save = async () => {
    if (!rows) return;
    setSaving(true);
    setProblem(null);
    try {
      await saveMcSettings({ agents: rows.filter((m) => m.takes_callbacks)
        .map((m) => ({ id: m.id, name: m.name, languages: m.languages.length ? m.languages : ['English'] })) });
      await onSaved();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save who takes callbacks');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel
      title="Who takes callbacks"
      subtitle="Your team in AgencyZoom. A callback goes to someone who speaks the client's language; if nobody does, to the first person ticked."
      loading={loading}
      loadingHeight={160}
      actions={canEdit && rows?.length ? (
        <ToolbarButton variant="outlined" icon={<SaveRounded />} onClick={save} loading={saving}
          disabled={!changed || !rows.some((m) => m.takes_callbacks)}>
          Save
        </ToolbarButton>
      ) : undefined}
    >
      {data?.team_error ? (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.danger })}>
          We couldn't read your team from AgencyZoom: {data.team_error}. Check AgencyZoom under Integrations.
        </Typography>
      ) : !rows?.length ? (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
          Nobody yet. Connect AgencyZoom under Integrations, and add your team there.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((m) => (
            <Box key={m.id} sx={{ display: 'grid', gap: 1.5, alignItems: 'center',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(220px, 1fr) 2fr' } }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  size="small"
                  checked={m.takes_callbacks}
                  disabled={!canEdit}
                  onChange={(e) => change(m.id, { takes_callbacks: e.target.checked })}
                  inputProps={{ 'aria-label': `${m.name} takes callbacks` }}
                />
                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{m.name}</Typography>
                  {m.email ? (
                    <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
                      {m.email}
                    </Typography>
                  ) : null}
                </Stack>
              </Stack>
              <FilterSelect
                label="Speaks"
                allLabel="Choose languages"
                // Any language saved before this list existed stays choosable.
                options={[...LANGUAGES, ...m.languages.filter((l) => !LANGUAGES.includes(l))]
                  .map((l) => ({ value: l, label: l }))}
                value={m.languages}
                onChange={(next) => change(m.id, { languages: LANGUAGES.filter((l) => next.includes(l))
                  .concat(next.filter((l) => !LANGUAGES.includes(l))) })}
                disabled={!canEdit || !m.takes_callbacks}
              />
            </Box>
          ))}
          {problem ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
          ) : null}
        </Stack>
      )}
    </Panel>
  );
}
